import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { color } from "../../../styles";
import dayjs, { Dayjs } from "dayjs";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { Box } from "@mui/material";

interface DateRangeFilterProps {
    onChangeValue: Function;
    isReset?: Boolean;
    height?: number | string;
    initialValue?: [string | null, string | null];
}

export default function DateRangeFilter({
    onChangeValue,
    isReset,
    height = 30,
    initialValue,
}: DateRangeFilterProps) {
    const parseDate = (rawValue: string | null): Dayjs | null => {
        if (!rawValue) return null;
        return dayjs(rawValue);
    };

    const [value, setValue] = React.useState<DateRange<Dayjs>>(() => {
        if (initialValue) {
            return [parseDate(initialValue[0]), parseDate(initialValue[1])];
        }
        return [null, null];
    });
    const controlHeight = typeof height === "number" ? `${height}px` : height;

    React.useEffect(() => {
        setValue((prev) => {
            if (!initialValue) return [null, null];
            const nextStart = parseDate(initialValue[0]);
            const nextEnd = parseDate(initialValue[1]);
            if (
                prev[0]?.format("YYYY-MM-DD") === nextStart?.format("YYYY-MM-DD") &&
                prev[1]?.format("YYYY-MM-DD") === nextEnd?.format("YYYY-MM-DD")
            ) {
                return prev;
            }
            return [nextStart, nextEnd];
        });
    }, [initialValue?.[0], initialValue?.[1]]);

    const onChangeDate = (newValue: any) => {
        setValue(newValue);
        onChangeValue(
            newValue.map((item: any) => {
                if (item) {
                    return item.format("YYYY-MM-DD");
                }
                return null;
            })
        );
    };

    React.useEffect(() => {
        if (isReset) {
            setValue([null, null]);
        }
    }, [isReset]);

    return (
        <Box
            data-testid="date-filter"
            sx={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                height: controlHeight,
                overflow: "visible",
                boxSizing: "border-box",
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
                <DemoContainer
                    components={["SingleInputDateRangeField"]}
                    sx={{
                        p: 0,
                        width: "100%",
                        minWidth: 0,
                        maxWidth: "100%",
                        height: controlHeight,
                        overflow: "visible",
                        boxSizing: "border-box",
                        "& .MuiStack-root": {
                            p: 0,
                            overflow: "visible",
                        },
                        "& .MuiTextField-root": {
                            minWidth: 0,
                        },
                    }}
                >
                    <DateRangePicker
                        value={value}
                        onChange={onChangeDate}
                        slotProps={{
                            textField: {
                                InputProps: {},
                            },
                            field: {
                                clearable: true,
                            } as any,
                        }}
                        slots={{
                            field: SingleInputDateRangeField,
                        }}
                        dayOfWeekFormatter={(date: Dayjs) => {
                                                const weekdaysShort = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
                                                return weekdaysShort[date.day()];
                                            }}
                        name="allowedRange"
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            "& .MuiInputBase-root": {
                                height: controlHeight,
                                fontSize: "12px",
                                fontStyle: "normal",
                                fontWeight: 400,
                                lineHeight: "normal",
                                fontFamily: "Noto Sans",
                                paddingRight: "4px",
                                paddingLeft: "0px",
                                width: "100%",
                                maxWidth: "100%",
                                overflow: "hidden",
                                boxSizing: "border-box",
                                display: "flex",
                                minWidth: 0,
                            },
                            "& .MuiFormControl-root": {
                                paddingRight: "0px",
                                width: "100%",
                                maxWidth: "100%",
                                boxSizing: "border-box",
                            },
                            "& .MuiInputBase-input": {
                                paddingLeft: "8px",
                                paddingRight: "8px",
                                paddingTop: "6px",
                                paddingBottom: "6px",
                                boxSizing: "border-box",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                minWidth: 0,
                                flex: "1 1 0%",
                                maxWidth: "100%",
                            },
                            "& .MuiInputBase-inputContainer": {
                                width: "100%",
                                minWidth: 0,
                                overflow: "hidden",
                            },
                            "& .MuiOutlinedInput-root": {
                                width: "100%",
                                maxWidth: "100%",
                                borderRadius: "5px",
                                overflow: "hidden",
                                boxSizing: "border-box",
                                minWidth: 0,
                                "& .MuiOutlinedInput-notchedOutline": {
                                    boxSizing: "border-box",
                                },
                                "& fieldset": {
                                    border: `1px solid ${color.secondary.BORDER}`,
                                    boxSizing: "border-box",
                                },
                                "&:hover fieldset": {
                                    border: `1px solid ${color.secondary.BORDER}`,
                                },
                                "&.Mui-focused fieldset": {
                                    border: `1px solid ${color.secondary.BORDER}`,
                                },
                            },
                        }}
                    />
                </DemoContainer>
            </LocalizationProvider>
        </Box>
    );
}
